import * as React from 'react';
import {useState, useMemo, useEffect, useRef} from 'react';
import {
  parsePhoneNumber,
  parseIncompletePhoneNumber,
  formatIncompletePhoneNumber as _formatIncompletePhoneNumber,
  getCountryCallingCode,
  getExampleNumber,
  parseDigits,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import type {PhoneNumber, CountryCode} from 'libphonenumber-js';
import {assignProps, view} from '@sail/engine';
import {arrowUpDown} from '@sail/icons';
import {tokens as $} from '@sail/tokens';
import type {View} from '../view';
import {css} from '../css';
import countryLabels from './addressData/countryLabels.json';

import type {CompositeFieldProps} from './CompositeField';
import {CompositeField} from './CompositeField';
import {DynamicIcon, Icon} from './Icon';
import {getFlagIcon} from './LazyIcons';
import {SelectField} from './SelectField';
import {TextField} from './TextField';
import {PopoverConfig} from './popover';
import {useFormRegister} from './form/useFormRegister';

import {useSelection} from './hooks/useSelection';
import type {InputWrapperProps} from './FormFieldGroup';
import {PhoneNumberFieldConfig} from './PhoneNumberFieldConfig';

export {CountryCode};

export type PhoneNumberFieldProps = Omit<
  CompositeFieldProps & View.IntrinsicElement<'fieldset'>,
  'onChange' | 'subviews'
> & {
  /**
   * An E.164 format telephone number, e.g. "+12024561111"
   */
  value?: string;

  /**
   * An E.164 format telephone number, e.g. "+12024561111"
   */
  defaultValue?: string;

  /**
   * A two-letter international country code (ISO 3166-1 alpha-2), e.g. "US"
   */
  defaultCountry: CountryCode;

  /**
   * A list of countries that are selectable from the country selector
   */
  countryAllowlist: CountryCode[];

  /**
   * Handler that is called when the input value is changed
   */
  onChange?: (value: string) => void;

  /**
   * Text that describes the control. Will be both visible and clickable.
   */
  label?: React.ReactNode;

  /**
   * Descriptive text that will be rendered adjacent to the control's label
   */
  description?: React.ReactNode;

  /**
   * Error text that will be rendered below the control
   */
  error?: React.ReactNode | void;

  /**
   * Disables the field and removes it from submission
   */
  disabled?: boolean;

  /**
   * Makes the field non-editable
   */
  readOnly?: boolean;

  /**
   * Sets the size of the field
   */
  size: 'small' | 'medium' | 'large';

  /**
   * Controls whether the control should be automatically focused
   */
  autoFocus?: boolean;

  subviews: View.Subviews<{
    inputs: (props: View.ViewProps<InputWrapperProps>) => JSX.Element | null;
    countryInput: typeof SelectField;
    countryCallingCode: 'span';
    phoneNumberInput: typeof TextField;
  }>;

  placeholder?: string;
};

function stripCountryCallingCode(value: string, countryCallingCode: string) {
  return value.replace(new RegExp(`^\\+?\\D*${countryCallingCode} ?`), '');
}

function formatNumber(phoneNumber: PhoneNumber) {
  return phoneNumber
    .formatInternational()
    .replace(/^\+\d*/, '')
    .trim();
}

function formatIncompletePhoneNumber(e164PhoneNumber: string) {
  return _formatIncompletePhoneNumber(e164PhoneNumber)
    .replace(/^\+\d*/, '')
    .trim();
}

function parseValue(value: string, defaultCountry: CountryCode) {
  let country: CountryCode | undefined;
  let countryCallingCode: string;
  let nationalNumber: string;
  let e164PhoneNumber: string;
  let formattedNumber: string | undefined;

  try {
    // first try to parse the number as-is
    const phoneNumber = parsePhoneNumber(value, defaultCountry);
    country = phoneNumber.country;
    countryCallingCode = phoneNumber.countryCallingCode;
    nationalNumber = phoneNumber.nationalNumber;
    e164PhoneNumber = phoneNumber.number;

    if (phoneNumber.isPossible()) {
      formattedNumber = formatNumber(phoneNumber);
    }
  } catch (err) {
    // if it fails (not enough input, or invalid input)
    countryCallingCode = getCountryCallingCode(defaultCountry);
    nationalNumber = parseIncompletePhoneNumber(
      parseDigits(stripCountryCallingCode(value, countryCallingCode)),
    );
    e164PhoneNumber = `+${countryCallingCode}${nationalNumber}`;
  }

  // if we couldn't get a formatted number, or what we got doesn't have any
  // formatting characters, let's format it assuming it's an incomplete number
  if (formattedNumber === undefined || !formattedNumber.match(/\D/)) {
    formattedNumber = formatIncompletePhoneNumber(e164PhoneNumber);
  }

  return {
    country,
    countryCallingCode,
    nationalNumber,
    e164PhoneNumber,
    formattedNumber,
  };
}

function digitCount(value: string) {
  return [...value.matchAll(/\d/g)].length;
}

function isPossibleE164PhoneNumber(value: string) {
  try {
    return value.startsWith('+') && parsePhoneNumber(value).isPossible();
  } catch (err) {
    return false;
  }
}

const FONT_MAP = {
  small: css({font: 'label.small'}),
  medium: css({font: 'label.medium'}),
  large: css({font: 'label.large'}),
};

export const PhoneNumberField = PhoneNumberFieldConfig.createView((originalProps) => {
  const {componentProps} = useFormRegister({props: originalProps});

  const {
    countryAllowlist,
    description,
    error,
    size,
    // TODO: use a locale provider to determine a better default country for the user
    defaultCountry,
    value: valueProp,
    defaultValue,
    onChange,
    name,
    autoFocus,
    placeholder: placeholderProp,
    disabled,
    readOnly,
    subviews,
    ...props
  } = componentProps as View.RenderProps<PhoneNumberFieldProps>;

  const [value, setValue] = useState(valueProp ?? defaultValue ?? '');
  const [country, setCountry] = useState(
    (countryAllowlist.length === 1 && countryAllowlist[0]) || defaultCountry,
  );
  const compositeFieldRef = useRef<HTMLDivElement>(null);

  const {
    country: parsedCountry,
    e164PhoneNumber,
    formattedNumber,
    nationalNumber,
    countryCallingCode,
  } = useMemo(() => parseValue(value, country), [value, country]);

  const placeholder = useMemo(() => {
    if (placeholderProp) {
      return placeholderProp;
    }

    const number = getExampleNumber(country, examples);
    if (number) {
      return formatNumber(number);
    }
  }, [country, placeholderProp]);

  const phoneNumberInputRef = React.useRef<HTMLInputElement>(null);
  const {setSelection} = useSelection(phoneNumberInputRef);

  useEffect(() => {
    valueProp && setValue(valueProp);
  }, [valueProp]);

  useEffect(() => {
    parsedCountry && setCountry(parsedCountry);
  }, [parsedCountry]);

  useEffect(() => {
    if (e164PhoneNumber !== value) {
      setValue(e164PhoneNumber);
      nationalNumber.length && onChange?.(e164PhoneNumber);
    }
  }, [e164PhoneNumber, nationalNumber, value, onChange]);

  const sortedCountryAllowlist = useMemo(() => {
    return countryAllowlist
      .map((countryCode) => ({
        id: countryCode,
        label: countryLabels[countryCode],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [countryAllowlist]);

  const countryList = useMemo(() => {
    const list = [];
    // If the selected country is not in the allowlist, add it as a disabled option at the top of the list
    if (!countryAllowlist.find((countryCode) => countryCode === country)) {
      list.push({
        id: country,
        label: countryLabels[country],
        disabled: true,
      });
    }
    list.push(...sortedCountryAllowlist);
    return list;
  }, [sortedCountryAllowlist, country, countryAllowlist]);

  return (
    <CompositeField
      {...props}
      description={description}
      error={error}
      size={size}
      disabled={disabled}
      subviews={{
        inputs: {
          uses: [
            assignProps({
              ref: compositeFieldRef,
            }),
          ],
          css:
            countryList.length > 1
              ? css({
                  padding: `0 ${$['space.small']} 0 0`,
                })
              : css({}),
          // @ts-expect-error - Error after migration to React 18
          ...subviews.inputs,
        },
      }}
    >
      {countryList.length > 1 ? (
        <SelectField
          inherits={subviews.countryInput}
          label="Country"
          data={countryList}
          item={{
            textValue: (item: object) => {
              const countryItem = item as {id: CountryCode; label: string};
              return `${countryItem.label} (+${getCountryCallingCode(
                countryItem.id,
              )})`;
            },
            icon: (item: object) => {
              const countryItem = item as {id: CountryCode};
              const FlagIcon = getFlagIcon(countryItem.id);
              return <DynamicIcon icon={FlagIcon} />;
            },
          }}
          selectedKey={country}
          disabledKeys={!countryAllowlist.includes(country) ? [country] : []}
          disabled={readOnly}
          filterable
          onSelectionChange={(selectionValue) => {
            const nextCountry = selectionValue as CountryCode;
            const nextCountryCallingCode = getCountryCallingCode(nextCountry);
            const nextE164PhoneNumber = `+${nextCountryCallingCode}${nationalNumber}`;
            setValue(nextE164PhoneNumber);
            setCountry(nextCountry);
            if (nationalNumber.length) {
              onChange?.(nextE164PhoneNumber);
            }
          }}
          css={{width: 'fit', minWidth: 'initial'}}
          size={size}
          uses={[
            PopoverConfig.customize({
              uses: [assignProps({targetRef: compositeFieldRef})],
            }),
          ]}
          subviews={{
            button: {
              css: {
                keyline: 'transparent',
                ':hover:not(:active):not([aria-disabled="true"])': {
                  keyline: 'transparent',
                },
                ':active:not([aria-disabled="true"])': {
                  backgroundColor: 'initial',
                },
                ':not(:focus)': {
                  boxShadow: 'none',
                },
              },
              uses: [
                assignProps({
                  children: (
                    <>
                      {country ? (
                        <DynamicIcon
                          data-testid="flag-icon"
                          data-value={country}
                          icon={getFlagIcon(country)}
                          size="small"
                        />
                      ) : null}
                      <Icon icon={arrowUpDown} aria-hidden size="xsmall" />
                    </>
                  ),
                  disabled,
                }),
              ],
            },
          }}
        />
      ) : null}
      <view.div
        css={[
          countryList.length > 1
            ? css({marginLeft: 'small'})
            : css({marginLeft: 'space.0'}),
          css({
            width: '20ch',
            stack: 'x',
            alignY: 'center',
          }),
        ]}
      >
        <view.span
          // TODO: SAIL-3215 no hardcoded strings
          aria-label="Country calling code"
          inherits={subviews.countryCallingCode}
          css={[
            css({
              marginRight: 'small',
              userSelect: 'none',
            }),
            FONT_MAP[size],
          ]}
        >
          +{countryCallingCode}
        </view.span>
        <TextField
          type="tel"
          ref={phoneNumberInputRef}
          inherits={subviews.phoneNumberInput}
          // TODO: SAIL-3215 no hardcoded strings
          aria-label="Phone number"
          value={formattedNumber}
          placeholder={placeholder}
          autoComplete="tel-national"
          autoFocus={autoFocus}
          readOnly={readOnly}
          onChange={(e) => {
            const input = e.target as HTMLInputElement;
            const cursorIndex = input.selectionStart ?? undefined;

            // If a fully-formed e.164 number was pasted in (where country is in countryAllowlist), use that.
            // Otherwise, prepend the current country code
            let valueToParse = `+${countryCallingCode} ${input.value}`;

            if (isPossibleE164PhoneNumber(input.value)) {
              const parsedPhone = parsePhoneNumber(input.value);

              if (
                parsedPhone?.country &&
                countryAllowlist.includes(parsedPhone.country)
              ) {
                valueToParse = input.value;
              }
            }

            const next = parseValue(valueToParse, country);

            if (
              // Reject any inputs that produce the same normalized value (e.g. attempting to delete
              // a formatting character, typing a non-digit, etc.)
              next.e164PhoneNumber === value ||
              // Reject any input additions that cause the value to lose its formatting.
              // (Workaround for libphonenumber-js behavior of accepting extra digits and
              // losing the formatting, e.g. (800) 555-1234 => 80055512345)
              (value.length < next.e164PhoneNumber.length &&
                formattedNumber.match(/\D+/) &&
                !next.formattedNumber.match(/\D+/))
            ) {
              if (cursorIndex !== undefined) {
                const diffLength = input.value.length - formattedNumber.length;
                setSelection(cursorIndex - Math.max(0, diffLength));
              }
              return;
            }

            // React doesn't know what to do with the cursor when updating a controlled input
            // so it just defaults it to the end of the input when it receives a new controlled value.
            // To maintain consistency when a user edits the value from somewhere in the middle,
            // we need to restore the cursor position to be after the same number of digits as before.
            const numDigitsBeforeCursor = digitCount(
              input.value.slice(0, cursorIndex),
            );

            // Get the set of digits and their indexes in the upcoming formatted input string.
            const matchedDigits = [...next.formattedNumber.matchAll(/\d/g)];

            const previousDigitCount = digitCount(input.value);
            const nextDigitCount = matchedDigits.length;

            // It's possible that some characters (such as leading digits) got stripped
            // so we need to account for them when setting the next cursor position.
            const digitCountDelta = previousDigitCount - nextDigitCount;

            // If the cursor precedes all digits (e.g. user deleted all input from the cursor
            // to the start of the value), keep the cursor at the start of the input.
            // Otherwise, place it after the same number of digits as before.
            setSelection(
              numDigitsBeforeCursor === 0
                ? 0
                : (matchedDigits[numDigitsBeforeCursor - digitCountDelta - 1]
                    ?.index ?? 0) + 1,
            );

            setValue(next.e164PhoneNumber);

            if (value !== next.e164PhoneNumber) {
              onChange?.(
                next.nationalNumber.length ? next.e164PhoneNumber : '',
              );
            }
          }}
        />
      </view.div>
      <view.input
        type="hidden"
        name={name}
        // we don't want value to ever be just the country calling code
        // since that would make it more difficult to do `required` field validation
        value={nationalNumber.length ? e164PhoneNumber : ''}
        disabled={disabled}
      />
    </CompositeField>
  );
});
